import { useState } from 'react';
import type { Receipt, ReceiptStatus } from '@salonomy/types';
import { Button, Card, EmptyState, Field, Pill, Select, Spinner, TextInput } from '@salonomy/ui';
import { useReceipts, useVoidReceipt } from '../../data/receipts.js';
import { useSalon } from '../../data/salon.js';
import { useAuth } from '../../lib/auth.js';
import { ApiClientError } from '../../lib/api.js';
import { money } from '../../lib/format.js';
import { useToast } from '../../lib/toast.js';
import { longDate, todayInZone, addDays } from '../../lib/time.js';
import { ReceiptActions } from '../till/ReceiptActions.js';
import './payments.css';

const STATUS_FILTERS: Array<{ value: '' | ReceiptStatus; label: string }> = [
  { value: '', label: 'All receipts' },
  { value: 'issued', label: 'Issued' },
  { value: 'voided', label: 'Voided' },
];

/**
 * WS6 — receipt history. Lists receipts in a window with a status filter (forwarded to the API), with a
 * detail panel that reuses the till's ReceiptActions for Print + Email and adds a manager-only Void with
 * a confirm. A VOIDED receipt can't be emailed or printed — ReceiptActions disables BOTH the email and
 * print buttons on a voided receipt, and the Void button hides once voided. Every figure (number,
 * totals) is the server's.
 */
export function ReceiptHistory() {
  const { data: salon } = useSalon();
  const tz = salon?.timezone ?? 'Europe/London';
  const today = todayInZone(tz);

  const [from, setFrom] = useState(() => addDays(today, -30));
  const [to, setTo] = useState(today);
  const [status, setStatus] = useState<'' | ReceiptStatus>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // The API filters on `issuedAt` and treats `to` exclusively, so pass the day AFTER `to` to make the
  // window inclusive of the chosen end date.
  const { data, isLoading, isError } = useReceipts({
    from: dayStartIso(from),
    to: dayStartIso(addDays(to, 1)),
    status: status || undefined,
  });

  const rows = data?.data ?? [];
  const selected = rows.find((r) => r.id === selectedId) ?? null;

  return (
    <Card>
      <div className="payments-head">
        <div>
          <div className="section-title" style={{ marginBottom: 2 }}>
            Receipt history
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            Reprint, resend or void any issued receipt. Voiding preserves the gapless number.
          </div>
        </div>
      </div>

      <div className="payments-filters">
        <Field label="From">
          <TextInput type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="To">
          <TextInput
            type="date"
            value={to}
            min={from}
            max={today}
            onChange={(e) => setTo(e.target.value)}
          />
        </Field>
        <Field label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value as '' | ReceiptStatus)}>
            {STATUS_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '16px 0' }}>
          <Spinner size={16} />
          <span className="muted" style={{ fontSize: 13 }}>
            Loading receipts…
          </span>
        </div>
      ) : isError ? (
        <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
          Couldn&rsquo;t load receipts. Try again in a moment.
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No receipts in this window"
          description="Widen the date range, or issue a receipt at the till."
        />
      ) : (
        <div className="receipts-split">
          <div className="receipts-list">
            {rows.map((r) => (
              <button
                key={r.id}
                className={`till-row${selected?.id === r.id ? ' till-row--active' : ''}`}
                onClick={() => setSelectedId(r.id)}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="client-row__name">
                    {r.receiptRef}
                    {r.status === 'voided' ? (
                      <Pill tone="danger" className="receipts-voidpill">
                        voided
                      </Pill>
                    ) : null}
                  </div>
                  <div className="client-row__sub">
                    {longDate(r.issuedAt.slice(0, 10), tz)} · {r.clientName}
                  </div>
                </div>
                <span className="mono">{money(r.grossTotalPence)}</span>
              </button>
            ))}
          </div>

          <div className="receipts-detail">
            {selected ? (
              <ReceiptDetail receipt={selected} />
            ) : (
              <div className="muted" style={{ fontSize: 13, padding: 12 }}>
                Select a receipt to reprint, resend or void it.
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function ReceiptDetail({ receipt }: { receipt: Receipt }) {
  const toast = useToast();
  const { isManager } = useAuth();
  const voidReceipt = useVoidReceipt();
  const [confirming, setConfirming] = useState(false);

  async function doVoid() {
    try {
      await voidReceipt.mutateAsync({ id: receipt.id });
      toast.success(`Receipt ${receipt.receiptRef} voided`);
      setConfirming(false);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not void the receipt');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 4 }}>
      <div>
        <div style={{ fontFamily: 'var(--slm-font-display)', fontSize: 20 }}>
          {receipt.receiptRef}
        </div>
        <div className="muted" style={{ fontSize: 13 }}>
          {receipt.clientName}
          {receipt.clientEmail ? ` · ${receipt.clientEmail}` : ''}
        </div>
      </div>

      <div className="till__line">
        <span>Subtotal (ex-VAT)</span>
        <span className="mono">{money(receipt.subtotalExVatPence)}</span>
      </div>
      <div className="till__line">
        <span>VAT</span>
        <span className="mono">{money(receipt.vatTotalPence)}</span>
      </div>
      <div className="till__line till__line--total">
        <span>Total</span>
        <span className="mono">{money(receipt.grossTotalPence)}</span>
      </div>

      {/* Print + Email (ReceiptActions disables BOTH email and print once the receipt is voided). */}
      <ReceiptActions receipt={receipt} />

      {/* Void: manager-only, with a confirm. A voided receipt can't be voided again or emailed/printed. */}
      {receipt.status === 'voided' ? (
        <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
          This receipt is voided — its gapless number is preserved, but it can&rsquo;t be emailed or
          reprinted as a valid receipt.
        </div>
      ) : isManager ? (
        confirming ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
              Void receipt <strong>{receipt.receiptRef}</strong>? This is corrective — the gapless
              number is kept, the receipt is never deleted. The client should be issued a fresh one
              if needed.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="danger" size="sm" onClick={doVoid} disabled={voidReceipt.isPending}>
                {voidReceipt.isPending ? 'Voiding…' : 'Void receipt'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setConfirming(false)}>
                Keep it
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={() => setConfirming(true)}>
            Void receipt
          </Button>
        )
      ) : (
        <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
          Voiding a receipt is a manager action. Ask an owner or manager.
        </div>
      )}
    </div>
  );
}

/** A salon-local date string → an ISO instant at UTC midnight (good enough for an issuedAt window). */
function dayStartIso(date: string): string {
  return new Date(`${date}T00:00:00.000Z`).toISOString();
}
